/**
 * A modal built on the native `<dialog>` element.
 *
 * `showModal()` brings focus containment, Escape-to-dismiss, inertness of the
 * page behind it and the top-layer stacking with it — all the parts of a dialog
 * that hand-rolled versions get subtly wrong. The only work left here is keeping
 * the element's own open state in step with React's, and translating the browser
 * events back into the `onClose` the parent already owns.
 */

import { useEffect, useId, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';

export interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Wider layout for forms with two columns of fields. */
  size?: 'default' | 'wide';
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'default',
}: ModalProps): ReactElement {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    // Escape closes the element directly; without this the DOM and React
    // disagree about whether the dialog is open and it cannot be reopened.
    const handleCancel = (event: Event): void => {
      event.preventDefault();
      onCloseRef.current();
    };

    // A click counts as "on the backdrop" only if it also STARTED there.
    // Without the pointerdown half, selecting text in a field and releasing
    // past the edge of the dialog closes it and throws the form away.
    let pressedBackdrop = false;

    const handlePointerDown = (event: PointerEvent): void => {
      pressedBackdrop = event.target === dialog;
    };

    const handleClick = (event: MouseEvent): void => {
      if (pressedBackdrop && event.target === dialog) onCloseRef.current();
      pressedBackdrop = false;
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('pointerdown', handlePointerDown);
    dialog.addEventListener('click', handleClick);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('pointerdown', handlePointerDown);
      dialog.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (dialog !== null && dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className={size === 'wide' ? 'modal modal--wide' : 'modal'}
      aria-labelledby={titleId}
      aria-describedby={description === undefined ? undefined : descriptionId}
    >
      <div className="modal__head">
        <h2 className="modal__title" id={titleId}>
          {title}
        </h2>
        {description !== undefined ? (
          <p className="modal__description" id={descriptionId}>
            {description}
          </p>
        ) : null}
      </div>

      <div className="modal__body">{children}</div>

      {footer !== undefined ? <div className="modal__foot">{footer}</div> : null}
    </dialog>
  );
}
